'use client';

import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { getPendingInvitations, inviteUser, deleteInvitation } from '../../redux/actions/userActions';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// Schéma de validation pour l'invitation
const InviteSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email invalide')
    .required('Email requis'),
  roleName: Yup.string()
    .required('Rôle requis')
});

const InvitationsList = () => {
  const dispatch = useDispatch();
  const { invitations, loading, error } = useSelector(state => state.users);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Charger les invitations
  useEffect(() => {
    dispatch(getPendingInvitations());
  }, [dispatch]);
  
  // Gérer l'invitation d'un utilisateur
  const handleInvite = async (values, { setSubmitting, resetForm }) => {
    try {
      await dispatch(inviteUser(values));
      setShowInviteModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de l\'invitation:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Gérer la suppression d'une invitation
  const handleDeleteClick = (invitation) => {
    setInvitationToDelete(invitation);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    if (!invitationToDelete) return;
    
    try {
      await dispatch(deleteInvitation(invitationToDelete.id));
      setShowDeleteModal(false);
      setInvitationToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };
  
  // Formater la date d'expiration
  const formatExpiryDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Vérifier si une invitation est expirée
  const isExpired = (dateString) => {
    const expiryDate = new Date(dateString);
    const now = new Date();
    return expiryDate < now;
  };
  
  return (
    <div className="invitations-list-container">
      <div className="page-header">
        <h1>Gestion des invitations</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowInviteModal(true)}
        >
          <i className="material-icons">person_add</i>
          Inviter un utilisateur
        </button>
      </div>
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des invitations...</p>
        </div>
      ) : invitations?.length > 0 ? (
        <div className="admin-card">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Date d'expiration</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(invitation => (
                  <tr key={invitation.id} className={isExpired(invitation.expiresAt) ? 'expired-row' : ''}>
                    <td>{invitation.email}</td>
                    <td>{formatExpiryDate(invitation.expiresAt)}</td>
                    <td>
                      <span className={`status-badge ${isExpired(invitation.expiresAt) ? 'rejected' : 'pending'}`}>
                        {isExpired(invitation.expiresAt) ? 'Expirée' : 'En attente'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button 
                          className="btn-icon"
                          title="Renvoyer l'invitation"
                          onClick={() => {
                            setShowInviteModal(true);
                            // Pré-remplir le formulaire avec l'email de l'invitation existante
                          }}
                        >
                          <i className="material-icons">refresh</i>
                        </button>
                        
                        <button 
                          className="btn-icon danger"
                          title="Supprimer l'invitation"
                          onClick={() => handleDeleteClick(invitation)}
                        >
                          <i className="material-icons">delete</i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <i className="material-icons">mail</i>
          <h2>Aucune invitation</h2>
          <p>Aucune invitation en attente n'a été trouvée.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowInviteModal(true)}
          >
            Inviter un utilisateur
          </button>
        </div>
      )}
      
      {/* Modal d'invitation */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Inviter un utilisateur</h3>
              <button
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            
            <Formik
              initialValues={{ email: '', roleName: 'user' }}
              validationSchema={InviteSchema}
              onSubmit={handleInvite}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="modal-body">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                      placeholder="email@exemple.com"
                    />
                    <ErrorMessage name="email" component="div" className="invalid-feedback" />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="roleName">Rôle *</label>
                    <Field
                      as="select"
                      name="roleName"
                      id="roleName"
                      className={`form-control ${errors.roleName && touched.roleName ? 'is-invalid' : ''}`}
                    >
                      <option value="user">Utilisateur</option>
                      <option value="admin">Administrateur</option>
                    </Field>
                    <ErrorMessage name="roleName" component="div" className="invalid-feedback" />
                  </div>
                  
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowInviteModal(false)}
                    >
                      Annuler
                    </button>
                    
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Envoi...' : 'Envoyer l\'invitation'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
      
      {/* Modal de confirmation de suppression */}
      {showDeleteModal && invitationToDelete && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Confirmer la suppression</h3>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer l'invitation pour <strong>{invitationToDelete.email}</strong> ?</p>
            </div>
            
            <div className="modal-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>
              
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationsList;